import { memo, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getStoreState, useAppStore, type AppState } from '../../store';
import { computeWires, type Wires } from '../../lib/wires';
import { mapContextOf } from './mapContext';
import { posY } from './posY';
import { countWireRender } from './renderCount';
import { subscribeCamera } from './mapController';
import { onWireRepaint } from './wireBus';
import { offsetOf } from './dragOffset';

/**
 * Every line on the map: the parent-child wires and the dotted cross-links.
 *
 * IT NEEDS NO DOM AT ALL, which is the finding this component is built on. `drawWires()`
 * re-measured two elements per edge with a forced reflow and ran on every `pointermove`
 * frame, but the only things it used were x, y, w, h, side and colour — every one of them a
 * value the store already holds. So the paths are arithmetic (`lib/wires.ts`), and this is
 * the thin React shell around them.
 *
 * THREE DIFFERENT UPDATE PATHS, deliberately, because they cost different amounts:
 *
 *  - **The camera** never re-renders anything. It is one `transform` on the wrapping `<g>`,
 *    written from a store subscription outside React. A pan must not reconcile 300 paths any
 *    more than it must reconcile 147 nodes.
 *  - **A drag** re-writes the `d` attributes in place, on the same frame, through
 *    `wireBus`. The set of paths does not change while a node is being dragged — only their
 *    shape — so there is nothing for React to reconcile and no reason to make it try.
 *  - **Everything else** — an expand, a search reveal, a new cross-link, a theme change —
 *    goes through a normal render, because the set of paths really did change.
 */

/** The values that decide the PATH LIST. The camera is deliberately absent. */
function wireInputsOf(s: AppState) {
  return {
    nodes: s.nodes,
    edges: s.edges,
    links: s.links,
    positions: s.positions,
    sizes: s.sizes,
    displayY: s.displayY,
    docsExpanded: s.docsExpanded,
    forceOpen: s.forceOpen,
    perm: s.perm,
    tagEdges: s.tagEdges,
    showLinks: s.showLinks,
    editing: s.editing,
    isLight: s.isLight,
  };
}

export function computeWiresFromState(s: AppState): Wires {
  const ctx = mapContextOf(s);
  return computeWires({
    nodes: s.nodes,
    edges: s.edges,
    links: s.links,
    // The drag offset is consulted here and nowhere else in the wire path: while a subtree is
    // being dragged its committed position is stale on purpose, and this is where the truth
    // is. See `dragOffset.ts`.
    positionOf: (id) => {
      const p = s.positions[id];
      if (!p) return undefined;
      const d = offsetOf(id);
      return d ? { x: p.x + d.dx, side: p.side } : p;
    },
    yOf: (id) => {
      const d = offsetOf(id);
      return posY(s, id) + (d ? d.dy : 0);
    },
    sizeOf: (id) => s.sizes[id],
    isHidden: ctx.isHidden,
    showLinks: s.showLinks,
    editing: s.editing,
    isLight: s.isLight,
  });
}

function WireLayerInner() {
  const inputs = useAppStore(useShallow(wireInputsOf));
  countWireRender();

  const wires = useMemo(() => computeWiresFromState(getStoreState()), [inputs]);

  const gRef = useRef<SVGGElement>(null);
  const edgeRefs = useRef(new Map<string, SVGPathElement>());
  const linkRefs = useRef(new Map<number, SVGPathElement[]>());

  const applyCamera = (view: { x: number; y: number; k: number }) => {
    const g = gRef.current;
    if (g) g.setAttribute('transform', `translate(${view.x},${view.y}) scale(${view.k})`);
  };

  // Written on mount and on every camera change, never through props: a camera write must not
  // re-render this component. `useLayoutEffect` so the first paint already has the transform.
  useLayoutEffect(() => {
    applyCamera(getStoreState().view);
  });
  useEffect(() => subscribeCamera(applyCamera), []);

  /**
   * Repaint in place — the drag path.
   *
   * Recomputes every path from the store plus whatever offset the drag is currently applying,
   * and writes the `d` attributes directly. No React render, no reflow, one frame.
   */
  useEffect(
    () =>
      onWireRepaint(() => {
        const next = computeWiresFromState(getStoreState());
        for (const w of next.edges) edgeRefs.current.get(w.id)?.setAttribute('d', w.d);
        for (const w of next.links) {
          for (const el of linkRefs.current.get(w.index) ?? []) el.setAttribute('d', w.d);
        }
      }),
    [],
  );

  edgeRefs.current = new Map();
  linkRefs.current = new Map();

  return (
    <g ref={gRef}>
      {wires.edges.map((w) => (
        <path
          key={w.id}
          // The CHILD's id: `edges` is inverted, so an edge is named by its child. It is here
          // because the drag repaints these by id, and because a wire that misses its anchor
          // is otherwise unaddressable — a test can only count paths and hope the order held.
          data-edge={w.id}
          ref={(el) => {
            if (el) edgeRefs.current.set(w.id, el);
          }}
          d={w.d}
          fill="none"
          stroke={w.stroke}
          strokeWidth={2}
          strokeOpacity={w.opacity}
          strokeLinecap="round"
        />
      ))}
      {wires.links.map((w) => (
        // The fat transparent hit-path must sit IMMEDIATELY before its own visible link:
        // `#wires .xlink-hit:hover + .xlink` is an adjacent-sibling rule, and anything
        // between them thickens the wrong line. A fragment keeps them paired.
        <WireLink
          key={w.index}
          index={w.index}
          d={w.d}
          stroke={w.stroke}
          editing={inputs.editing}
          register={(el) => {
            const list = linkRefs.current.get(w.index) ?? [];
            list.push(el);
            linkRefs.current.set(w.index, list);
          }}
        />
      ))}
    </g>
  );
}

function WireLink({
  index,
  d,
  stroke,
  editing,
  register,
}: {
  index: number;
  d: string;
  stroke: string;
  editing: boolean;
  register: (el: SVGPathElement) => void;
}) {
  return (
    <>
      {editing ? (
        <path
          className="xlink-hit"
          data-link={index}
          d={d}
          fill="none"
          stroke="transparent"
          strokeWidth={16}
          ref={(el) => {
            if (el) register(el);
          }}
        />
      ) : null}
      <path
        className="xlink"
        data-link={index}
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={2.2}
        strokeDasharray="3 6"
        strokeLinecap="round"
        ref={(el) => {
          if (el) register(el);
        }}
      />
    </>
  );
}

export const WireLayer = memo(WireLayerInner);
